import React from 'react';
import moment from 'moment';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TimeSeries } from 'pondjs';
import { scaleLinear, scaleLog } from 'd3-scale';

import { roundAndExponent } from '../../../shared/utils/math';

const fontSize = '12px';
const margin = 25;
const width = 500;
const border = 2;
const padding = 3;
const useStyles = makeStyles((theme) => ({
  tooltipContainer: {
    width: `${width}px`,
    marginLeft: `${margin}px`,
    marginRight: `${margin}px`,
    padding: `${padding}px`,
    position: 'absolute',
    pointerEvents: 'none',
    background: 'white',
    border: `solid ${border}px #999`,
    fontSize,

    display: 'grid',
    gridTemplateAreas: `
      '. time .'
      'bar text value'
`,
    gridTemplateRows: 'auto auto',
    gridTemplateColumns: '20px auto',
    rowGap: theme.spacing(1),
    columnGap: theme.spacing(1),
  },
  metricLabelTime: {
    gridArea: 'time',
  },
  metricLabelTimeStyle: {
    fontSize,
  },
  metricLabelBar: {
    gridArea: 'bar',
    alignSelf: 'center',
    width: '100%',
    height: '5px',
  },
  metricLabelText: {
    gridArea: 'text',
    wordBreak: 'break-all',
  },
  metricLabelValue: {
    gridArea: 'value',
    alignSelf: 'center',
  },
}));

interface MonitoringTooltipProps {
  xPosition: number;
  yPosition: number;
  tracker: Date;
  chartData: Record<string, TimeSeries>;
  selectorToColorMapping: Record<string, string>;
  isLogScale: boolean;
  height: number;
  axisMargin: number;
  min: number;
  max: number;
  bandChartData?: TimeSeries;
}

const MonitoringTooltip: React.FC<MonitoringTooltipProps> = ({
  chartData,
  xPosition,
  yPosition,
  tracker,
  selectorToColorMapping,
  isLogScale,
  height,
  axisMargin,
  min,
  max,
  bandChartData,
}: MonitoringTooltipProps) => {
  const innerHeight = height - axisMargin * 2;
  const rangeTop = axisMargin;
  const rangeBottom = innerHeight - axisMargin;
  const scale = !isLogScale
    ? scaleLinear().domain([min, max]).range([rangeBottom, rangeTop]).nice()
    : scaleLog().base(10).domain([min, max]).range([rangeBottom, rangeTop]).nice();

  let minimum = Number.MAX_VALUE;
  let tooltipColor: string | undefined;
  let tooltipValue: number | undefined;
  let displayedSelector: string | undefined;
  Object.entries(chartData).forEach(([selector, timeseries]) => {
    const index = timeseries.bisect(new Date(tracker), 0);
    const value = timeseries.at(index).get('value') as number | null;
    if (value !== null && Math.abs(scale.invert(yPosition) - value) < minimum) {
      minimum = Math.abs(scale.invert(yPosition) - value);
      tooltipColor = `${selectorToColorMapping[selector]}`;
      displayedSelector = selector;
      tooltipValue = value;
    }
  });

  let bandChartLow: number | null = null;
  let bandChartHigh: number | null = null;
  if (typeof bandChartData !== 'undefined') {
    const index = bandChartData.bisect(new Date(tracker), 0);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    bandChartLow = bandChartData.at(index).get('bounds')[0] as number | null;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    bandChartHigh = bandChartData.at(index).get('bounds')[1] as number | null;
  }

  const classes = useStyles();

  if (tracker && typeof tooltipColor !== 'undefined' && typeof tooltipValue !== 'undefined') {
    return (
      <div
        className={classes.tooltipContainer}
        style={{ left: xPosition < 550 ? xPosition : xPosition - width - 2 * (margin + border + padding) }}
      >
        <div className={classes.metricLabelTime}>
          <Typography className={classes.metricLabelTimeStyle} color="textSecondary" variant="body2">
            <b>{moment(tracker).format('DD-MMM-YYYY hh:mm:ss a')}</b>
          </Typography>
        </div>
        <div
          className={classes.metricLabelBar}
          style={{
            backgroundColor: tooltipColor,
          }}
        />
        <div className={classes.metricLabelText}>{displayedSelector}</div>
        <div className={classes.metricLabelValue}>
          <b>{`${roundAndExponent(tooltipValue)}`}</b>{' '}
          {bandChartLow != null && bandChartHigh != null && bandChartHigh !== bandChartLow && (
            <>{`(${roundAndExponent(bandChartLow)} | ${roundAndExponent(bandChartHigh)})`}</>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default MonitoringTooltip;
